import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  Grid,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { selectUser } from '../store/slices/authSlice';
import { showSuccessMessage, showErrorMessage } from '../store/slices/uiSlice';
import { authService } from '../services/authService';
import { User, UpdateUserRequest, ChangePasswordRequest } from '../types/auth';

const ProfilePage: React.FC = () => {  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  // Profile form state
  const [profileForm, setProfileForm] = useState<UpdateUserRequest>({
    username: currentUser?.username || '',
    email: currentUser?.email || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    phoneNumber: currentUser?.phoneNumber || '',
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        username: currentUser.username,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phoneNumber: currentUser.phoneNumber || '',
      });
    }
  }, [currentUser]);  const handleProfileSave = async () => {
    if (!currentUser?.id) return;

    // Validation
    if (!profileForm.firstName?.trim()) {
      dispatch(showErrorMessage('First name is required'));
      return;
    }

    if (!profileForm.lastName?.trim()) {
      dispatch(showErrorMessage('Last name is required'));
      return;
    }

    if (!profileForm.email?.trim()) {
      dispatch(showErrorMessage('Email is required'));
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileForm.email)) {
      dispatch(showErrorMessage('Please enter a valid email address'));
      return;
    }

    // Phone validation (if provided)
    if (profileForm.phoneNumber && profileForm.phoneNumber.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(profileForm.phoneNumber.replace(/\s|-/g, ''))) {
        dispatch(showErrorMessage('Please enter a valid phone number'));
        return;
      }
    }

    try {
      setLoading(true);
        await authService.updateUser(currentUser.id, profileForm);
      dispatch(showSuccessMessage('Profile updated successfully!'));
      setIsEditing(false);
      
      // Note: In a real app, you might want to update the Redux store with the new user data
    } catch (err: any) {
      console.error('Profile update error:', err);
      
      let errorMessage = 'Failed to update profile';
      
      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Invalid profile data';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again';
      } else if (err.response?.status === 409) {
        errorMessage = 'Email or username already exists';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      dispatch(showErrorMessage(errorMessage));
    } finally {
      setLoading(false);
    }
  };const handlePasswordChange = async () => {
    // Validation
    if (!passwordForm.currentPassword.trim()) {
      dispatch(showErrorMessage('Current password is required'));
      return;
    }

    if (!passwordForm.newPassword.trim()) {
      dispatch(showErrorMessage('New password is required'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      dispatch(showErrorMessage('New passwords do not match'));
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      dispatch(showErrorMessage('New password must be at least 8 characters long'));
      return;
    }

    // Password strength validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(passwordForm.newPassword)) {
      dispatch(showErrorMessage('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'));
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      dispatch(showErrorMessage('New password must be different from current password'));
      return;
    }

    try {
      setLoading(true);
      
      await authService.changePassword(passwordForm);
      
      dispatch(showSuccessMessage('Password changed successfully! You will be logged out for security.'));
      setIsPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
    } catch (err: any) {
      console.error('Password change error:', err);
      
      let errorMessage = 'Failed to change password';
      
      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Current password is incorrect';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again';
      } else if (err.response?.status === 422) {
        errorMessage = 'Password does not meet security requirements';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      dispatch(showErrorMessage(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (currentUser) {
      setProfileForm({
        username: currentUser.username,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phoneNumber: currentUser.phoneNumber || '',
      });
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString();
  };
  if (!currentUser) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">Unable to load user profile</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Profile Information</Typography>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleProfileSave}
                      disabled={loading}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
              </Stack>              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    disabled={!isEditing || loading}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    disabled={!isEditing || loading}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                    disabled={!isEditing || loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                    disabled={!isEditing || loading}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profileForm.phoneNumber}
                    onChange={(e) => setProfileForm({ ...profileForm, phoneNumber: e.target.value })}
                    disabled={!isEditing || loading}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>        {/* Profile Summary Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                }}
              >
                {getInitials(currentUser.firstName, currentUser.lastName)}
              </Avatar>
              
              <Typography variant="h6" gutterBottom>
                {currentUser.fullName || `${currentUser.firstName} ${currentUser.lastName}`}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                @{currentUser.username}
              </Typography>

              <Stack direction="row" justifyContent="center" spacing={1} mb={3}>
                {currentUser.roles?.map((role) => (
                  <Chip
                    key={role.id}
                    label={role.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Joined: {formatDate(currentUser.createdAt)}
                  </Typography>
                </Stack>
                
                {currentUser.lastLoginAt && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Last login: {formatDate(currentUser.lastLoginAt)}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setIsPasswordDialogOpen(true)}
                sx={{ mt: 3 }}
              >
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SecurityIcon />
            <Typography variant="h6">Change Password</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              disabled={loading}
              helperText="Password must be at least 6 characters long"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              disabled={loading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsPasswordDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePasswordChange}
            disabled={loading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
